import React, { createContext, useContext } from 'react';
import { Link } from 'react-router-dom';
import NavPinStar from './NavPinStar';
import { PRODUCTS } from '../layout/Sidebar/productConfig';

/**
 * A sidebar link with its pin star beside it, for the classic sidebar, whose ~120
 * links are hand-written: it imports this as `Link`, so every row gets a star
 * without restructuring each one. The sidebar provides one pins hook through
 * NavPinsContext (one storage subscription, not one per row).
 *
 * Only `nav-link` rows get a star, and only when their text gives a label;
 * anything else (logo, buttons styled as links) renders as a plain Link.
 */
export const NavPinsContext = createContext(null);

/** The visible text of a link's children (icons render none). */
export const textOf = (node) => {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join(' ');
  if (node.props) return textOf(node.props.children);
  return '';
};

/** The product a path belongs to, by its longest matching route prefix. */
export const moduleForPath = (path) => {
  let best = null;
  PRODUCTS.forEach((p) => (p.routePrefixes || []).forEach((prefix) => {
    if (path.startsWith(prefix) && (!best || prefix.length > best.len)) best = { len: prefix.length, label: p.label };
  }));
  return best ? best.label : '';
};

const PinnableNavLink = ({ to, className = '', children, ...rest }) => {
  const pins = useContext(NavPinsContext);
  const link = <Link to={to} className={className} {...rest}>{children}</Link>;
  const path = typeof to === 'string' ? to : to && to.pathname;
  if (!pins || !path || !/(^|\s)nav-link(\s|$)/.test(className)) return link;
  const label = textOf(children).replace(/\s+/g, ' ').trim();
  if (!label) return link;
  return (
    <>
      {link}
      <NavPinStar
        className="nav-pin-star--overlay"
        label={label}
        pinned={pins.isPinned(path)}
        full={pins.isFull}
        onToggle={() => pins.toggle({ path, label, module: moduleForPath(path) })}
      />
    </>
  );
};

export default PinnableNavLink;
